import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Lock, Mail } from 'lucide-react';
import { login } from '../../utils/authUtils';
import { Dialog } from '../admin/Dialog';
import axios from 'axios';


const PageContainer = styled.div`
  min-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  padding: 12px;
`;

const LoginCard = styled.div`
  max-width: 400px;
  width: 100%;
  background-color: white;
  padding: 2rem;
  border-radius: 0px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`;

const Title = styled.h2`
  margin-top: 1.5rem;
  text-align: center;
  font-size: 1.875rem;
  font-weight: 800;
  color: #1f2937;
`;

const Subtitle = styled.p`
  margin-top: 0.5rem;
  text-align: center;
  font-size: 0.875rem;
  color: #4b5563;
`;

const Form = styled.form`
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Input = styled.input`
  appearance: none;
  border: 1px solid #d1d5db;
  border-radius: 0px;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  width: 100%;
  color: #1f2937;

  &:focus {
    outline: none;
    border-color: #47ABDB;
    box-shadow: 0 0 0 3px rgba(71, 171, 219, 0.15);
  }
`;

const ErrorMessage = styled.div`
  background-color: #fee2e2;
  border: 1px solid #f87171;
  color: #b91c1c;
  padding: 0.75rem;
  border-radius: 0px;
  font-size: 0.875rem;
`;

const Button = styled.button`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0px;
  font-size: 0.875rem;
  font-weight: 500;
  color: white;
  background-color: #47ABDB;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: #0056b3;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(71, 171, 219, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ForgotPasswordLink = styled.a`
  font-size: 0.875rem;
  color: #47ABDB;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const SuccessMessage = styled.div`
  background-color: #d1fae5;
  border: 1px solid #34d399;
  color: #065f46;
  padding: 0.75rem;
  border-radius: 0px;
  font-size: 0.875rem;
`;

/**
 * Shared login form for admin and speaker portals. Both roles authenticate
 * against the same /api/authenticate endpoint; only the copy and the
 * post-login redirect differ.
 */
const LoginForm = ({ title, subtitle, redirectTo }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('session_expired') === 'true') {
      setError('Your session has expired. Please login again.');
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      await login(username, password);
      navigate(redirectTo);
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message === 'Session expired. Please login again.'
        ? 'Your session has expired. Please login again.'
        : 'Invalid credentials or server error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/password/reset`, { email });
      setResetEmailSent(true);
    } catch (err) {
      console.error('Password reset error:', err);
      setError('Failed to send password reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageContainer>
      <LoginCard>
        <Title>{title}</Title>
        <Subtitle>{subtitle}</Subtitle>
        <Form onSubmit={handleLogin}>
          <InputContainer>
            <Input
              id="username"
              name="username"
              type="text"
              required
              placeholder="Username or email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </InputContainer>
          <InputContainer>
            <Input
              id="password"
              name="password"
              type="password"
              required
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </InputContainer>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <Button type="submit" disabled={isLoading}>
            <Lock size={20} />
            {isLoading ? 'Signing in...' : 'Sign in'}
          </Button>
        </Form>
        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
          <ForgotPasswordLink onClick={() => setShowForgotPassword(true)}>
            Forgot password?
          </ForgotPasswordLink>
        </div>
      </LoginCard>

      <Dialog
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        title="Reset Password"
        description="Enter your email address to receive a password reset link."
      >
        <form onSubmit={handleForgotPassword}>
          <Input
            id="email"
            name="email"
            type="email"
            required
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{ marginBottom: '1rem' }}
          />
          {resetEmailSent ? (
            <SuccessMessage>
              Password reset email sent. Please check your inbox.
            </SuccessMessage>
          ) : (
            <Button type="submit" disabled={isLoading}>
              <Mail size={20} />
              {isLoading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          )}
        </form>
      </Dialog>
    </PageContainer>
  );
};

export default LoginForm;
